<template>
    <div>      
        <portal to="aux-modal">
            <MollieNoProfileModal 
                v-if="getMollie.showModal && getMollie.status !== 'ok'"
                :status="getMollie.status"
                @close="onCancelMollieModal"
            />
            <RootlineErrorModal 
                v-if="getRootline.showModal && getRootline.status !== 'verified'"
                :status="getRootline.status"
                @close="onCancelRootlineModal"
            />
        </portal>
        <div v-for="aPaymentMethod in getPaymentMethods" :key="aPaymentMethod.service">
            <b-form-checkbox
                :id="'checkbox-paymentlink-' + aPaymentMethod.service"
                v-if="!aPaymentMethod.checked"
                v-model="aPaymentMethod.checked"
                @input="attachPayment(aPaymentMethod.service)"
                :name="'checkbox-paymentlink-' + aPaymentMethod.service"
                :disabled="aPaymentMethod.disabled">
                {{ $gettext('Add %s payment link').replace("%s", aPaymentMethod.service) }}
                <font-awesome-icon :id="'tooltip-paymentlink-'+ aPaymentMethod.service" class="tooltip-icon tooltip-icon--question ml-1" icon="fa-regular fa-circle-question" />
                <b-tooltip :custom-class="'tooltip-padded'" :target="'tooltip-paymentlink-'+ aPaymentMethod.service" triggers="hover" container="#report-container">
                  <div class="tooltip-text" v-html="$gettext('<span style=\'color: white; font-weight: bold; text-decoration: underline;\'>Pay this invoice directly using a payment link.</span> This will add a payment link to your email that allows your customer to pay this invoice directly via online payment.')" />
                </b-tooltip>
            </b-form-checkbox>
        </div>
        <div v-if="!isPremium" class="pt-3">
            <div class="gk-tip gk-tip--warning gk-tip--blue mb-3">
                <p>{{ $gettext('Adding a payment link will on average make your customer pay your invoice 3 times faster. Premium users can send payment links for free and will only pay the Mollie fee for successful payments. Basic users pay an aditional 50 cents per successful transaction. Consider upgrading.') }}</p>
            </div>
        </div>
        
        <div v-if="hasActivePayments && currencyNotEuro" class="text-info" v-translate>You cannot send payment links for invoices that are not in Euro.</div>

        <div v-if="getMollie.checked && !getMollie.amountOK" class="text-info" v-translate>You cannot send payment links for invoices with negative totals.</div>
        <div v-if="getRootline.checked && !getRootline.amountOK" class="text-info" v-translate>You cannot send payment links for invoices with totals smaller than 1 euro.</div>
        
        <div v-if="getMollie.checked && getMollie.message" class="text-danger">{{ getMollie.message }}</div>
        <div v-if="getRootline.checked && getRootline.message" class="text-danger">{{ getRootline.message }}</div>
    </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from "vuex";
import IdealPaymentService from "@/store/services/idealpayment.service";
import CompanySettingService from "@/store/services/companysetting.service";
import RootlineErrorModal from "@/components/invoices/ui/RootlineErrorModal"
import MollieNoProfileModal from "@/components/invoices/ui/MollieNoProfileModal"

export default {
    name: "PaymentLink",
    components: {
        MollieNoProfileModal,
        RootlineErrorModal,
    },
    props: {
        currencyNotEuro: {
            type: Boolean,
            default: false
        },
        invoiceAmount: {
            type: Number,
            default: 0
        },
        value: {
            type: Object,
            required: true,
        },
        fallbackAttachMollie: {
            type: Boolean,
            default: false
        },
        fallbackAttachRootline: {
            type: Boolean,
            default: false
        },
    },
    data() {
        return {
            defaultService: 'mollie',
            paymentMethods: [],
            activeServices: ["mollie", "rootline"],
        }
    },
    watch: {
        'localSendDetails.attach_mollie_payment' (newVal, oldVal) {
            this.getMollie.checked = newVal;
            if(!newVal){
                this.updateCompanySettings('mollie', false);
            }
        },
        'localSendDetails.attach_rootline_payment' (newVal, oldVal) {
            this.getRootline.checked = newVal;
            if(!newVal){
                this.updateCompanySettings('rootline', false);
            }
        },
    },
    computed: {
        ...mapGetters({ 
            currentCompany: "main/getCurrentCompany",
            isPremium: 'getIsPremium',
        }),
        localSendDetails: {
            get () { return this.value },
            set (localSendDetails) { this.$emit('input', localSendDetails) }
        },
        hasActivePayments(){
            return this.paymentMethods.some(el => el.active)
        },
        getMollie(){
            return this.paymentMethods.filter((el)=>{return el.service==="mollie"})[0];
        },
        getRootline(){
            return this.paymentMethods.filter((el)=>{return el.service==="rootline"})[0];
        },
        getPaymentMethods(){
            if(!this.hasActivePayments){
                return this.paymentMethods.filter((el)=>{return el.service===this.defaultService})
            }
            var default_service = this.paymentMethods.filter((el)=>{return el.service===this.defaultService});
            if(default_service[0].active){
                return default_service
            }
            if(this.defaultService === "mollie"){
                return this.paymentMethods.filter((el)=>{return el.service==="rootline"});
            }else{
                return this.paymentMethods.filter((el)=>{return el.service==="mollie"});
            }
        }
    },
    created() {
        this.initialize();
    },
    methods: {
        ...mapMutations ({ 
            setCompanySettings: "main/SET_CURRENT_COMPANY_SETTINGS",
        }),
        initialize() {
            var count = 0;
            this.activeServices.forEach((a_service) => {
                var temp = {
                    id: count,
                    service: a_service,
                    checked: false,
                    status: null,
                    active: false,
                    message: '',
                    amountOK: false,
                    disabled: false
                }
                if(a_service == "mollie"){
                    temp.amountOK = this.invoiceAmount > 0;
                }else if (a_service == "rootline"){
                    temp.amountOK = this.invoiceAmount > 1;  // min amount is 1 euro for rootline
                }
                if(this.currencyNotEuro){
                    temp.disabled = temp.amountOK;
                }
                this.paymentMethods.push(temp);
                count += 1;
            });
            this.initMollie()
            this.initRootline()
        },
        initMollie(){
            if(!this.activeServices.includes("mollie")){
                return;
            }
            var mollieState = this.getMollie;
            IdealPaymentService.getConnectionStatus().then((response) => {
                switch (response.status) {
                    case 200:
                        mollieState.active = true;
                        mollieState.status = 'ok'
                        if(this.currentCompany.companysettings.attach_mollie_payment){
                            mollieState.checked = true;
                        }else{
                            mollieState.checked = this.fallbackAttachMollie;
                        }
                        break;
                    case 204:
                    case 298:
                        mollieState.status = 'no-profile'
                        mollieState.message = this.$gettext('Your Mollie profile could not be found.')
                        break;
                    case 299:
                        mollieState.status = 'missing-data'
                        mollieState.message = this.$gettext('Your Mollie profile is missing data.')
                        break;
                }
            }).catch((error) => {
                mollieState.status = 'mollie-error';
                mollieState.message = this.$gettext('Your Mollie connection is not working right now.')
            }).finally(()=>{
                if(mollieState.active && mollieState.checked){
                    this.attachPayment('mollie');
                }else{
                    this.updateCompanySettings('mollie', false);
                }
            })
        },
        initRootline(){
            if(!this.activeServices.includes("rootline")){
                return;
            }
            var rootlineState = this.getRootline;
            IdealPaymentService.getRootlineConnectionStatus().then((response) => {
                if(response.status == 204){
                    rootlineState.status = 'not-found'
                    rootlineState.message = this.$gettext('Your Rootline profile was not found.')
                }else{
                    rootlineState.status = response.data.status
                    switch(rootlineState.status){
                        case 'started':
                            rootlineState.message = this.$gettext('Your Rootline\'s connection request is being processed, please contact our support team if it takes more than 2 business days.')
                            break;
                        case 'failed':
                            rootlineState.message = this.$gettext('Error verifying Rootline account.')
                            break;
                        case 'disconnected':
                            rootlineState.message = this.$gettext('Your Rootline account was disconnected.')
                            break;
                        default:    // verified
                            rootlineState.active = true;
                            if(this.currentCompany.companysettings.attach_rootline_payment){
                                rootlineState.checked = true;
                            }else{
                                rootlineState.checked = this.fallbackAttachRootline;
                            }
                    }
                }
            }).catch(error => {
                rootlineState.status = 'error'
            }).finally(()=>{
                if(rootlineState.active && rootlineState.checked){
                    this.attachPayment('rootline');
                }else{
                    this.updateCompanySettings('rootline', false);
                }

            })
        },
        updateCompanySettings(service, attach){
            var companySettingId = this.currentCompany.companysettings.id;
            var dict_key = 'attach_%s_payment'.replace("%s", service);
            var data = {};
            data[dict_key] = attach
            CompanySettingService.partialUpdate(companySettingId, data).then((response) => {
                // update stored company settings
                this.setCompanySettings(response.data)
            })
        },
        attachPayment(service){
            if(service === 'rootline'){
                if(this.getRootline.status !== 'verified'){
                    this.getRootline.showModal = true
                    return;
                }
            }else if(service === 'mollie'){
                this.getMollie.showModal = false;
                switch (this.getMollie.status) {
                    case 'no-profile':
                        this.getMollie.showModal = true
                        this.getMollie.status = 'no-profile'
                        break;
                    case 'missing-data':
                        this.getMollie.showModal = true
                        this.getMollie.status = 'missing-data'
                        break;
                    case 'ok':
                        break;
                }
            }
            if(this.getMollie.showModal || this.getRootline.showModal){
                return;
            }
            var dict_key = 'attach_%s_payment'.replace("%s", service);
            this.localSendDetails[dict_key] = true;
            this.updateCompanySettings(service, true);
        },
        onCancelRootlineModal() {
            this.getRootline.showModal = false;
            this.getRootline.checked = false;
        },
        onCancelMollieModal(){
            this.getMollie.showModal = false;
            this.getMollie.checked = false;
        },
    }
}
</script>

<style lang="scss" scoped>
</style>